/**
 * Defines the scope/activation model for an agent rule.
 * - ALWAYS: Rule is always active regardless of context
 * - FILE_PATTERN: Rule activates when the AI is working on files matching the patterns
 */
export const AGENT_RULE_SCOPE = {
  ALWAYS: "always",
  FILE_PATTERN: "file-pattern",
} as const;

export type AgentRuleScope =
  (typeof AGENT_RULE_SCOPE)[keyof typeof AGENT_RULE_SCOPE];

/**
 * Supported AI coding assistant platforms.
 */
export const AGENT_PLATFORM = {
  CURSOR: "cursor",
  CLAUDE: "claude",
  CODEX: "codex",
  COPILOT: "copilot",
} as const;

export type AgentPlatform =
  (typeof AGENT_PLATFORM)[keyof typeof AGENT_PLATFORM];

/**
 * Render target for Claude Code rules.
 * - SCOPED_FILE: `.claude/rules/{name}.md` (default — supports paths frontmatter for conditional activation)
 * - AGENTS_MD: AGENTS.md (always-active, shared with Codex)
 * - CLAUDE_MD: CLAUDE.md (always-active, Claude-only content not consumed by other agents)
 */
export const CLAUDE_RULE_TARGET = {
  SCOPED_FILE: "scoped-file",
  AGENTS_MD: "agents-md",
  CLAUDE_MD: "claude-md",
} as const;

export type ClaudeRuleTarget =
  (typeof CLAUDE_RULE_TARGET)[keyof typeof CLAUDE_RULE_TARGET];

/**
 * Model selection for sub-agents. Platforms map these to their own model identifiers.
 */
export const AGENT_MODEL = {
  INHERIT: "inherit",
  FAST: "fast",
  BALANCED: "balanced",
  POWERFUL: "powerful",
} as const;

export type AgentModel = (typeof AGENT_MODEL)[keyof typeof AGENT_MODEL];

/**
 * Maps abstract AGENT_MODEL values to Claude Code model aliases.
 * Returns undefined for "inherit" (omit the field entirely).
 * Cursor omits the model field to use its default model selection.
 */
export function resolveModelAlias(
  model: string | undefined,
): string | undefined {
  if (!model || model === AGENT_MODEL.INHERIT) {
    return undefined;
  }
  const mapping: Record<string, string> = {
    [AGENT_MODEL.POWERFUL]: "opus",
    [AGENT_MODEL.BALANCED]: "sonnet",
    [AGENT_MODEL.FAST]: "haiku",
  };
  return mapping[model] ?? model;
}

/**
 * MCP server transport type.
 */
export const MCP_TRANSPORT = {
  STDIO: "stdio",
  HTTP: "http",
  SSE: "sse",
} as const;

export type McpTransport = (typeof MCP_TRANSPORT)[keyof typeof MCP_TRANSPORT];

/**
 * Platform-specific overrides for a rule.
 */
export interface AgentPlatformOverrides {
  /** Cursor-specific overrides */
  readonly cursor?: {
    /** Override the description used in Cursor's YAML frontmatter */
    readonly description?: string;
    /** Exclude this rule from Cursor output entirely */
    readonly exclude?: boolean;
  };
  /** Claude Code-specific overrides */
  readonly claude?: {
    /**
     * Where to render this rule for Claude Code.
     * - SCOPED_FILE: `.claude/rules/{name}.md` (default — supports paths frontmatter)
     * - AGENTS_MD: AGENTS.md (always-active, shared with Codex)
     * - CLAUDE_MD: CLAUDE.md (always-active, Claude-only)
     */
    readonly target?: ClaudeRuleTarget;
    /** Exclude this rule from Claude Code output entirely */
    readonly exclude?: boolean;
  };
  /** Codex-specific overrides (future) */
  readonly codex?: {
    /** Place this rule in a sub-directory AGENTS.md instead of root */
    readonly directory?: string;
    /** Exclude this rule from Codex output entirely */
    readonly exclude?: boolean;
  };
  /** Copilot-specific overrides (future) */
  readonly copilot?: {
    /** Exclude this rule from Copilot output entirely */
    readonly exclude?: boolean;
  };
}

/**
 * A single agent rule definition, platform-agnostic.
 */
export interface AgentRule {
  /**
   * Unique identifier for the rule. Used as the filename stem in platforms
   * that use per-rule files (Cursor, Claude Code, Copilot).
   * @example 'typescript-conventions'
   */
  readonly name: string;

  /**
   * Human-readable description of the rule's purpose.
   * Used by Cursor for AI-assisted rule selection.
   * @example 'TypeScript project patterns and conventions'
   */
  readonly description: string;

  /**
   * Activation scope for this rule.
   * - AGENT_RULE_SCOPE.ALWAYS: Active in all contexts
   * - AGENT_RULE_SCOPE.FILE_PATTERN: Active only when working on matching files
   */
  readonly scope: AgentRuleScope;

  /**
   * Glob patterns for conditional activation.
   * Required when scope is AGENT_RULE_SCOPE.FILE_PATTERN.
   * @example ['src/**\/*.ts', 'tests/**\/*.ts']
   */
  readonly filePatterns?: ReadonlyArray<string>;

  /**
   * The rule content as markdown. This is the platform-agnostic body
   * that applies equally to all AI assistants.
   */
  readonly content: string;

  /**
   * Optional per-platform overrides. Use sparingly — prefer platform-agnostic content.
   */
  readonly platforms?: AgentPlatformOverrides;

  /**
   * Optional tags for categorizing and ordering rules.
   * Rules are ordered by tag (alphabetical), then by name within each tag.
   * @example ['typescript', 'testing', 'workflow']
   */
  readonly tags?: ReadonlyArray<string>;
}

/**
 * A skill definition following the cross-platform Agent Skills specification.
 * Rendered to .claude/skills/ (Claude Code) and .cursor/skills/ (Cursor).
 */
export interface AgentSkill {
  /**
   * Unique identifier for the skill. Becomes the /slash-command name.
   * @example 'commit'
   */
  readonly name: string;

  /**
   * Human-readable description. Claude uses this to decide when to auto-invoke.
   */
  readonly description: string;

  /**
   * Multi-line instruction content (markdown). Becomes the SKILL.md body.
   */
  readonly instructions: string;

  /**
   * Optional tool allowlist for this skill.
   * @example ['Read', 'Bash(npm run *)']
   */
  readonly allowedTools?: ReadonlyArray<string>;

  /**
   * Whether to prevent auto-invocation of this skill.
   * When true, only triggered by the user typing /skill-name.
   * @default false
   */
  readonly disableModelInvocation?: boolean;

  /**
   * Whether the user can invoke this skill directly via /skill-name.
   * Set to false for background skills that should not appear in the / menu.
   * @default true
   */
  readonly userInvocable?: boolean;

  /**
   * Model override when this skill is active.
   * @example 'claude-opus-4-6'
   */
  readonly model?: string;

  /**
   * Reasoning effort level when this skill is active.
   * @example 'high'
   */
  readonly effort?: string;

  /**
   * Glob patterns that limit when the skill is auto-loaded.
   * @example ['src/api/**\/*.ts']
   */
  readonly paths?: ReadonlyArray<string>;

  /**
   * Resource directories bundled with the skill (e.g., references/, scripts/, assets/).
   * Documentation hint only — directory names listed here are not emitted to disk.
   * Prefer {@link referenceFiles} to ship actual companion file contents.
   *
   * @deprecated Use {@link referenceFiles} to emit physical companion files.
   * @example ['references/', 'scripts/']
   */
  readonly references?: ReadonlyArray<string>;

  /**
   * Companion files shipped alongside the SKILL.md (e.g., templates, references, scripts).
   * Each entry is rendered as a TextFile under the skill's directory on every
   * platform that emits skills (Claude: `.claude/skills/{name}/{path}`,
   * Cursor: `.cursor/skills/{name}/{path}`).
   *
   * Use this for skill assets that benefit from being separate files rather than
   * inlined into the skill instructions — large templates, reference tables,
   * runnable scripts.
   *
   * Per-platform `platforms.{claude,cursor}.exclude` flags suppress reference
   * files for that platform alongside the SKILL.md.
   *
   * @example
   * ```ts
   * referenceFiles: [
   *   { path: '_references/templates/_template-FR.md', content: '# Functional Requirement\n...' },
   *   { path: '_references/standards-and-frameworks.md', content: '# Standards\n...' },
   * ]
   * ```
   */
  readonly referenceFiles?: ReadonlyArray<{
    /** Path relative to the skill directory (e.g., `_references/templates/_template-FR.md`). */
    readonly path: string;
    /** File contents written verbatim. */
    readonly content: string;
  }>;

  /**
   * Context isolation mode. Set to 'fork' to run in an isolated subagent context.
   */
  readonly context?: string;

  /**
   * Subagent name to delegate to when context is 'fork'.
   * @example 'code-reviewer'
   */
  readonly agent?: string;

  /**
   * Shell for dynamic context injection via !`command` syntax.
   * @default 'bash'
   */
  readonly shell?: string;

  /** Per-platform overrides. Use `exclude: true` to skip a platform. */
  readonly platforms?: {
    readonly claude?: { readonly exclude?: boolean };
    readonly cursor?: { readonly exclude?: boolean };
  };
}

/**
 * Platform-specific overrides for a sub-agent definition.
 */
/**
 * Copilot handoff definition for sub-agent delegation.
 */
export interface CopilotHandoff {
  /** Display label for the handoff action. */
  readonly label: string;
  /** Target agent name. */
  readonly agent: string;
  /** Optional prompt to pass to the target agent. */
  readonly prompt?: string;
  /** Whether to auto-send the handoff without user confirmation. */
  readonly send?: boolean;
}

export interface AgentSubAgentPlatformOverrides {
  /** Claude Code-specific overrides */
  readonly claude?: {
    /** Permission mode: default, acceptEdits, dontAsk, bypassPermissions, plan */
    readonly permissionMode?: string;
    /** Run in isolated git worktree */
    readonly isolation?: string;
    /** Run as a background task */
    readonly background?: boolean;
    /** Reasoning effort level (Opus): low, medium, high, max */
    readonly effort?: string;
    /** Persistent memory scope: user, project, local */
    readonly memory?: string;
    /** Exclude this sub-agent from Claude Code output entirely */
    readonly exclude?: boolean;
  };
  /** Cursor-specific overrides */
  readonly cursor?: {
    /** Restrict the sub-agent to read-only operations */
    readonly readonly?: boolean;
    /** Run the sub-agent asynchronously as a background task */
    readonly isBackground?: boolean;
    /** Exclude this sub-agent from Cursor output entirely */
    readonly exclude?: boolean;
  };
  /** Codex-specific overrides (future) */
  readonly codex?: {
    /** Sandbox mode: read-only or full */
    readonly sandboxMode?: string;
    /** Model reasoning effort */
    readonly modelReasoningEffort?: string;
    /** Exclude this sub-agent from Codex output entirely */
    readonly exclude?: boolean;
  };
  /** Copilot-specific overrides (future) */
  readonly copilot?: {
    /** Target environment: vscode or github-copilot */
    readonly target?: string;
    /** Whether the user can invoke this sub-agent directly */
    readonly userInvocable?: boolean;
    /** Prevent auto-invocation of this sub-agent */
    readonly disableModelInvocation?: boolean;
    /** Handoff definitions for agent-to-agent delegation */
    readonly handoffs?: ReadonlyArray<CopilotHandoff>;
    /** Exclude this sub-agent from Copilot output entirely */
    readonly exclude?: boolean;
  };
}

/**
 * A custom sub-agent definition, platform-agnostic.
 * Rendered to .cursor/agents/ (Cursor), .claude/agents/ (Claude Code).
 */
export interface AgentSubAgent {
  /**
   * Unique identifier for the sub-agent. Used as the filename stem.
   * Must be lowercase letters and hyphens only.
   * @example 'code-reviewer'
   */
  readonly name: string;

  /** Human-readable description. Used by the parent agent to decide when to delegate. */
  readonly description: string;

  /**
   * System prompt / instructions for the sub-agent (markdown).
   * This becomes the body of the generated agent file.
   */
  readonly prompt: string;

  /**
   * Model selection for this sub-agent.
   * @default AGENT_MODEL.INHERIT
   */
  readonly model?: AgentModel;

  /**
   * Tool allowlist. When omitted, inherits all tools from parent.
   * @example ['Read', 'Glob', 'Grep']
   */
  readonly tools?: ReadonlyArray<string>;

  /**
   * Tool denylist. Applied before the allowlist.
   * @example ['Bash', 'Write']
   */
  readonly disallowedTools?: ReadonlyArray<string>;

  /** Maximum agentic turns before the sub-agent stops. */
  readonly maxTurns?: number;

  /**
   * Skills to preload for this sub-agent.
   * @example ['commit', 'review-pr']
   */
  readonly skills?: ReadonlyArray<string>;

  /**
   * MCP servers available to this sub-agent.
   * Rendered to the platform-specific sub-agent config.
   */
  readonly mcpServers?: Readonly<Record<string, McpServerConfig>>;

  /** Optional per-platform overrides for this sub-agent. */
  readonly platforms?: AgentSubAgentPlatformOverrides;
}

/**
 * A user-invokable slash command. Rendered to `.claude/commands/<name>.md`.
 *
 * Slash commands appear in the Claude Code command picker (typed as
 * `/<name>`) and execute the body as the user prompt. The body may
 * reference any rule, skill, sub-agent, or procedure registered via
 * `AgentConfigOptions` — bundle-shipped commands typically delegate to
 * an existing sub-agent or procedure rather than restating its content.
 *
 * @see https://docs.claude.com/en/docs/claude-code/slash-commands
 */
export interface AgentCommand {
  /**
   * Slash-command name (no leading slash). Lowercase, kebab-case.
   * Becomes the filename stem under `.claude/commands/`.
   * @example 'orchestrate'
   */
  readonly name: string;

  /**
   * One-sentence summary shown in the slash-command picker.
   */
  readonly description: string;

  /**
   * Body content (markdown). Rendered as the command file body verbatim,
   * after the generated YAML frontmatter.
   */
  readonly content: string;

  /**
   * Optional model override for the command's invocation. Maps to a
   * Claude Code model alias (e.g. `opus`, `sonnet`, `haiku`) via
   * {@link resolveModelAlias}.
   */
  readonly model?: AgentModel;
}

/**
 * An executable procedure (shell script) that ships with a bundle.
 * Rendered to `.claude/procedures/{name}` as an executable file.
 */
export interface AgentProcedure {
  /**
   * Filename for the procedure (e.g., 'check-blocked.sh').
   * Used as the filename in .claude/procedures/.
   */
  readonly name: string;

  /** Human-readable description of what this procedure does. */
  readonly description: string;

  /** Script content as a single string. Lines are split on newlines for rendering. */
  readonly content: string;
}

/**
 * MCP server configuration. Cross-platform — rendered to .claude/settings.json
 * (Claude Code) and .cursor/mcp.json (Cursor).
 */
export interface McpServerConfig {
  /**
   * Transport type for the server connection.
   * @default MCP_TRANSPORT.STDIO
   */
  readonly transport?: McpTransport;

  /** Command to launch a stdio server. */
  readonly command?: string;

  /** Command arguments for stdio server. */
  readonly args?: ReadonlyArray<string>;

  /** URL for HTTP/SSE remote servers. */
  readonly url?: string;

  /** HTTP headers for HTTP/SSE connections. */
  readonly headers?: Readonly<Record<string, string>>;

  /** Environment variables for the server process. */
  readonly env?: Readonly<Record<string, string>>;

  /**
   * Tool allowlist — only these tools from the server will be available.
   * @example ['read_file', 'search']
   */
  readonly enabledTools?: ReadonlyArray<string>;

  /**
   * Tool denylist — these tools from the server will be blocked.
   * Applied before enabledTools.
   * @example ['delete_file', 'execute']
   */
  readonly disabledTools?: ReadonlyArray<string>;
}
